import { Checkbox, Dropdown, Field, FixedOptionTextOutput, NumericInput, TextOutput } from 'Components/Core';
import { IFieldProps } from 'Components/Core/Field/types';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import {
  WorksheetBatchUpdateEquipmentDisplayField,
  WorksheetBatchUpdateEquipmentSelectionField,
  WorksheetBatchUpdateImportSourceField,
} from 'Components/WorksheetBatchUpdate/Core/SpecialisedFieldComponents';
import { WorksheetBatchUpdateWorksheetFieldSection } from 'Components/WorksheetBatchUpdate/types';
import { FdReportResultsOption } from 'Components/Worksheets/SandReplacementWorksheet/types';
import { FormatTypes } from 'Lib/Format';
import React from 'react';
import { WorksheetBatchUpdateWorksheetField } from '../..';

export const BsNuclearFieldDensityWorksheetSections: {
  [name: string]: WorksheetBatchUpdateWorksheetFieldSection;
} = {
  preparation: {
    heading: 'Preparation',
  },
  laboratoryDensity: {
    heading: 'Laboratory Density Results',
  },
  measurements: {
    heading: 'Measurements',
  },
  results: {
    heading: 'Results',
  },
};

export const BsNuclearFieldDensityWorksheetFields: {
  [name: string]: WorksheetBatchUpdateWorksheetField;
} = {
  'data.Preparation.MeasurementApplication': {
    displayComponent: <Field input={<FixedOptionTextOutput />} />,
  },
  'data.Preparation.NuclearDensityGauge': {
    displayComponent: <WorksheetBatchUpdateEquipmentDisplayField />,
    entryComponent: <WorksheetBatchUpdateEquipmentSelectionField equipmentType="nuclear-density-gauge" />,
    displayFieldsWhenEntered: [
      'data.Preparation.NuclearDensityGaugeSerialNumber',
      'data.Preparation.NuclearDensityGaugeMakeAndModel',
    ],
    isImportSource: true,
    equipmentDetails: {
      calibrationImportMismatchField: 'data.Preparation.NuclearDensityGaugeCalibratedImportMismatch',
      propertiesImportMismatchField: 'data.Preparation.NuclearDensityGaugePropertiesImportMismatch',
    },
    section: 'preparation',
  },
  'data.Preparation.NuclearDensityGaugeSerialNumber': {
    displayComponent: <Field input={<TextOutput />} />,
  },
  'data.Preparation.NuclearDensityGaugeMakeAndModel': {
    displayComponent: <Field input={<TextOutput />} />,
  },
  'data.Preparation.ApplyCorrections': {
    displayComponent: <Field input={<TextOutput />} format={{ type: FormatTypes.YesNo }} />,
    entryComponent: <Field input={<Checkbox />} />,
    defaultValue: false,
    displayFieldsWhenEntered: [
      'data.Preparation.BulkDensityCorrectionSlope',
      'data.Preparation.BulkDensityCorrectionIntercept',
      'data.Preparation.MoistureDensityCorrectionSlope',
      'data.Preparation.MoistureDensityCorrectionIntercept',
      'data.Measurements.AverageReadings.MeanWetDensity',
      'data.Measurements.AverageReadings.MeanMoistureDensity',
      'data.Results.InsituDryDensity',
      'data.Results.RelativeCompaction',
      'data.Results.InsituAirVoids',
    ],
    section: 'preparation',
  },
  'data.Preparation.BulkDensityCorrectionSlope': {
    displayComponent: <Field input={<TextOutput />} />,
    entryComponent: <Field input={<NumericInput />} noRecase horizontalLabelWidth={'long'} />,
    displayFieldsWhenEntered: [
      'data.Preparation.ApplyCorrections',
      'data.Measurements.AverageReadings.MeanWetDensity',
      'data.Results.InsituDryDensity',
      'data.Results.RelativeCompaction',
      'data.Results.InsituAirVoids',
    ],
    section: 'preparation',
  },
  'data.Preparation.BulkDensityCorrectionIntercept': {
    displayComponent: <Field input={<TextOutput />} />,
    entryComponent: <Field input={<NumericInput />} horizontalLabelWidth={'long'} />,
    displayFieldsWhenEntered: [
      'data.Preparation.ApplyCorrections',
      'data.Measurements.AverageReadings.MeanWetDensity',
      'data.Results.InsituDryDensity',
      'data.Results.RelativeCompaction',
      'data.Results.InsituAirVoids',
    ],
    section: 'preparation',
  },
  'data.Preparation.MoistureDensityCorrectionSlope': {
    displayComponent: <Field input={<TextOutput />} />,
    entryComponent: <Field input={<NumericInput />} noRecase horizontalLabelWidth={'long'} />,
    displayFieldsWhenEntered: [
      'data.Preparation.ApplyCorrections',
      'data.Measurements.AverageReadings.MeanMoistureDensity',
    ],
    section: 'preparation',
  },
  'data.Preparation.MoistureDensityCorrectionIntercept': {
    displayComponent: <Field input={<TextOutput />} />,
    entryComponent: <Field input={<NumericInput />} horizontalLabelWidth={'long'} />,
    displayFieldsWhenEntered: [
      'data.Preparation.ApplyCorrections',
      'data.Measurements.AverageReadings.MeanMoistureDensity',
    ],
    section: 'preparation',
  },
  'data.IsParticleDensityAssumed': {
    displayComponent: <Field input={<FixedOptionTextOutput />} />,
    entryComponent: <Field input={<Dropdown />} simpleValue isClearable={false} />,
    defaultValue: true,
    displayFieldsWhenEntered: [
      'data.Preparation.MeasurementApplication',
      'data.ParticleDensityAssumed',
      'data.ReferenceDensityResults.ParticleDensity',
      'data.Results.InsituAirVoids',
    ],
    section: 'laboratoryDensity',
  },
  'data.ParticleDensityAssumed': {
    displayComponent: <Field input={<TextOutput />} />,
    entryComponent: <Field input={<NumericInput />} />,
    displayFieldsWhenEntered: [
      'data.Preparation.MeasurementApplication',
      'data.IsParticleDensityAssumed',
      'data.Results.InsituAirVoids',
    ],
    section: 'laboratoryDensity',
  },
  'data.ReferenceDensity.ImportedTest': {
    /**
     * Special display component - extract meaningful string from imported test model
     */
    displayComponent: React.createElement(
      withInjectedProps<IFieldProps>(({ value, ...otherFieldProps }) => (
        <Field
          {...otherFieldProps}
          input={<TextOutput />}
          value={value?.Results?.ReferenceId ?? value?.Sample?.SampleId}
        />
      ))
    ),
    entryComponent: <WorksheetBatchUpdateImportSourceField importPropertyName={'ReferenceDensity'} />,
    alsoSets: ['data.ReferenceDensity.AssociatedTestType'],
    displayFieldsWhenEntered: [
      'data.Preparation.MeasurementApplication',
      'data.ReferenceDensityResults.ParticleDensity',
      'data.Results.MaximumDryDensity',
      'data.Results.OptimumMoistureContent',
      'data.Results.RelativeCompaction',
      'data.Results.InsituAirVoids',
    ],
    isImportSource: true,
    section: 'laboratoryDensity',
  },
  'data.ReferenceDensityResults.ParticleDensity': {
    displayComponent: <Field input={<TextOutput />} />,
    section: 'laboratoryDensity',
  },
  'data.Measurements.NuclearDensityGaugeMode': {
    displayComponent: <Field input={<FixedOptionTextOutput />} />,
  },
  'data.Measurements.Depth': {
    displayComponent: <Field input={<TextOutput />} />,
    entryComponent: <Field input={<NumericInput />} />,
    displayFieldsWhenEntered: ['data.Measurements.NuclearDensityGaugeMode'],
    section: 'measurements',
  },
  'data.Measurements.AverageReadings.MeanWetDensity': {
    displayComponent: <Field input={<TextOutput />} />,
  },
  'data.Measurements.AverageReadings.MeanMoistureDensity': {
    displayComponent: <Field input={<TextOutput />} />,
  },
  'data.Results.InsituDryDensity': {
    displayComponent: <Field input={<TextOutput />} />,
  },
  'data.Results.MaximumDryDensity': {
    displayComponent: <Field input={<TextOutput />} />,
  },
  'data.Results.OptimumMoistureContent': {
    displayComponent: <Field input={<TextOutput />} />,
  },
  'data.Results.RelativeCompaction': {
    displayComponent: <Field input={<TextOutput />} />,
  },
  'data.Results.InsituAirVoids': {
    displayComponent: <Field input={<TextOutput />} />,
  },
  'data.Results.ReportResultsOption': {
    displayComponent: <Field input={<FixedOptionTextOutput />} />,
    entryComponent: <Field input={<Dropdown />} simpleValue isClearable={false} />,
    defaultValue: FdReportResultsOption.RelativeCompactionOnly,
    displayFieldsWhenEntered: [
      'data.Preparation.MeasurementApplication',
      'data.Results.RelativeCompaction',
      'data.Results.InsituAirVoids',
    ],
    section: 'results',
  },
};

export default BsNuclearFieldDensityWorksheetFields;
